import { useCallback, useEffect, useRef, useState } from 'react';
import { useEmails } from '../../useEmails';
import { FiDownload, FiX } from 'react-icons/fi';
import { RxDragHandleHorizontal } from 'react-icons/rx';

/**
 * @param {string} url
 * @returns {string | null}
 */
const extractFilename = (url) => {
	try {
		const urlObj = new URL(url, window.location.origin);
		const pathname = urlObj.pathname;
		return pathname.substring(pathname.lastIndexOf('/') + 1);
	} catch (error) {
		console.error('Invalid URL:', error);
		return null;
	}
};

/**
 * @param {string} filename
 * @returns {string}
 */
const getExtension = (filename) => (filename ? (filename.split('.').pop() ?? '').toLowerCase() : '');

/**
 * Fetches a URL into a blob and returns an object URL — bypasses
 * Content-Disposition: attachment so the browser will preview inline.
 * @param {string | undefined} url
 * @param {boolean} active
 */
const useBlobUrl = (url, active) => {
	const [blobUrl, setBlobUrl] = useState(/** @type {string | null} */ (null));
	const [error, setError] = useState(/** @type {Error | null} */ (null));

	useEffect(() => {
		if (!active || !url) {
			setBlobUrl(null);
			setError(null);
			return;
		}
		let cancelled = false;
		let createdUrl = '';
		setError(null);
		fetch(url, { mode: 'cors', credentials: 'omit' })
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.blob();
			})
			.then((blob) => {
				if (cancelled) return;
				const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
				createdUrl = URL.createObjectURL(pdfBlob);
				setBlobUrl(createdUrl);
			})
			.catch((err) => {
				if (!cancelled) setError(err);
			});
		return () => {
			cancelled = true;
			if (createdUrl) URL.revokeObjectURL(createdUrl);
		};
	}, [url, active]);

	return { blobUrl, error };
};

const btnStyle = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 6,
	padding: '6px 12px',
	fontSize: 13,
	background: '#fff',
	border: '1px solid #d1d5db',
	borderRadius: 6,
	color: '#111827',
	cursor: 'pointer',
	textDecoration: 'none',
};

const iconBtnStyle = {
	width: 32,
	height: 32,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: 'transparent',
	border: 'none',
	borderRadius: 6,
	color: '#374151',
	cursor: 'pointer',
};

const docWrapStyle = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'flex-start',
	background: '#fff',
	borderRadius: 8,
	border: '1px solid #e5e7eb',
	padding: 12,
};

/**
 * @param {{ ispdfShow: boolean, selectedPdf: string | undefined }} props
 */
function PdfPreview({ ispdfShow, selectedPdf }) {
	const { handlepdfClose } = useEmails();
	const { blobUrl, error: blobError } = useBlobUrl(selectedPdf, ispdfShow);

	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const dragOffset = useRef({ x: 0, y: 0 });

	useEffect(() => {
		if (ispdfShow) setPosition({ x: 0, y: 0 });
	}, [ispdfShow]);

	useEffect(() => {
		if (!ispdfShow) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, [ispdfShow]);

	const handleMouseDown = useCallback(
		/** @param {import('react').MouseEvent} e */
		(e) => {
			e.preventDefault();
			setIsDragging(true);
			dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
		},
		[position]
	);

	useEffect(() => {
		if (!isDragging) return;
		/** @param {MouseEvent} e */
		const onMove = (e) => {
			setPosition({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
		};
		const onUp = () => setIsDragging(false);
		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
		return () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
		};
	}, [isDragging]);

	if (!ispdfShow || !selectedPdf) return null;

	const rawName = decodeURIComponent(extractFilename(selectedPdf) || '');
	const displayName = rawName.length > 60 ? rawName.slice(0, 60) + '...' : rawName;
	const ext = getExtension(rawName);
	const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
	const isPdf = ext === 'pdf' || !ext;
	const isDoc = ['doc', 'docx'].includes(ext);

	return (
		<div
			role='dialog'
			aria-modal='true'
			style={{
				position: 'fixed',
				inset: 0,
				zIndex: 1050,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<div
				onClick={handlepdfClose}
				style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.5)' }}
			/>
			<div
				style={{
					position: 'relative',
					zIndex: 1,
					width: '80vw',
					maxWidth: 1000,
					maxHeight: '90vh',
					background: '#fff',
					borderRadius: 8,
					boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					transform: `translate(${position.x}px, ${position.y}px)`,
				}}
			>
				<div
					style={{
						padding: '16px 20px',
						borderBottom: '1px solid #e5e7eb',
						display: 'flex',
						alignItems: 'center',
						gap: 12,
						flexShrink: 0,
					}}
				>
					<div
						onMouseDown={handleMouseDown}
						title='Drag to move'
						style={{
							cursor: isDragging ? 'grabbing' : 'grab',
							padding: 4,
							marginLeft: -4,
							color: '#6b7280',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						<RxDragHandleHorizontal size={20} />
					</div>
					<h2
						style={{
							fontSize: 15,
							fontWeight: 600,
							margin: 0,
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							color: '#111827',
						}}
					>
						{displayName}
					</h2>
					<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
						<a style={btnStyle} href={selectedPdf} target='_blank' rel='noopener noreferrer' download>
							<FiDownload size={14} />
							Download
						</a>
						<button style={iconBtnStyle} onClick={handlepdfClose} aria-label='Close'>
							<FiX size={16} />
						</button>
					</div>
				</div>

				<div style={{ flex: 1, overflow: 'auto', padding: 20, background: '#f9fafb' }}>
					{isPdf && blobError && (
						<div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
							Unable to load PDF.{' '}
							<a href={selectedPdf} target='_blank' rel='noopener noreferrer'>
								Open in new tab
							</a>
						</div>
					)}
					{isPdf && !blobError && !blobUrl && (
						<div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
							Loading preview…
						</div>
					)}
					{isPdf && blobUrl && (
						<iframe
							src={blobUrl}
							title={displayName}
							style={{
								width: '100%',
								height: '75vh',
								border: '1px solid #e5e7eb',
								borderRadius: 8,
								background: '#fff',
							}}
						/>
					)}
					{isImage && (
						<div style={docWrapStyle}>
							<img
								src={selectedPdf}
								alt={displayName}
								style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
							/>
						</div>
					)}
					{isDoc && (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								gap: 12,
								padding: '24px 0',
							}}
						>
							<div style={docWrapStyle}>{displayName}</div>
							<p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>
								Preview not available for Word documents
							</p>
							<a style={btnStyle} href={selectedPdf} target='_blank' rel='noopener noreferrer' download>
								<FiDownload size={14} />
								Download File
							</a>
						</div>
					)}
					{!isPdf && !isImage && !isDoc && (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								gap: 12,
								padding: '24px 0',
							}}
						>
							<div style={docWrapStyle}>{displayName}</div>
							<p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>
								Preview not available for this file type
							</p>
							<a style={btnStyle} href={selectedPdf} target='_blank' rel='noopener noreferrer' download>
								<FiDownload size={14} />
								Download File
							</a>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default PdfPreview;
